import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useQueryClient } from '@tanstack/react-query';

import { completeGatehouseCallback } from '../auth/gatehouse-oauth';

/**
 * Mounted at /integrations/auth/callback. Exchanges the OAuth code for
 * a Gatehouse access_token, stores it in localStorage, then navigates
 * the user back to wherever they came from. Failure shows an inline
 * message rather than a hard redirect — easier to diagnose than a
 * silent bounce back to /login.
 */
export function GatehouseCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  // The exchange only needs to run once per mount. StrictMode in dev
  // double-invokes effects, so we guard with a ref so the single-use
  // ?code= isn't burned twice.
  const ranRef = React.useRef(false);

  React.useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let cancelled = false;
    (async () => {
      const result = await completeGatehouseCallback(location.search);
      if (cancelled) return;
      if (!result.ok) {
        setError(result.error ?? 'Sign-in failed');
        return;
      }
      // Force the existing useUser query to refetch with the new
      // bearer so AuthProvider's user state hydrates immediately.
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
      navigate(result.redirectTo, { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [location.search, navigate, queryClient]);

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: 600, margin: '0 auto' }}>
        <Alert severity="error">
          <div>{error}</div>
          <a href="/integrations/login" style={{ marginTop: '1rem', display: 'inline-block' }}>
            Back to sign in
          </a>
        </Alert>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <CircularProgress />
    </div>
  );
}
